import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import {
  CustomerReviewStars,
  CustomerReviewStatusBadge,
} from '@/components/customer-reviews/customer-review-display'
import {
  IndexDataTable,
  getIndexPaginationMeta,
  IndexTableClearFiltersButton,
  IndexTablePagination,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import { useIndexTableSearchParams } from '@/components/tables/useIndexTableSearchParams'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import dayjs from '@/lib/dayjs'
import { CustomerReviewStatus } from '@/lib/enums/CustomerReviewStatus'
import queryClient from '@/lib/queryClient'
import { prefetchIndexTableRouteQuery } from '@/lib/services/index-table-query-options'
import {
  customerReviewQueries,
  useCustomerReviews,
} from '@/lib/services/customer-reviews/customer-reviews.query'
import { cn } from '@/lib/utils'
import { createFileRoute, notFound, stripSearchParams, useNavigate } from '@tanstack/react-router'
import {
  ArrowDownAZ,
  BadgeCheck,
  CalendarClock,
  CheckCircle2,
  EyeOff,
  Mail,
  MessageSquareText,
  SlidersHorizontal,
  Star,
  UserRound,
  XCircle,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { z } from 'zod/v4'

const DEFAULT_PAGE_SIZE = 20
const defaultReviewListSearchParams = {
  status: 'all',
  read_state: 'all',
  sort: 'received_desc',
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
}
const strippedReviewListSearchParams = {
  status: defaultReviewListSearchParams.status,
  read_state: defaultReviewListSearchParams.read_state,
  page: defaultReviewListSearchParams.page,
  page_size: defaultReviewListSearchParams.page_size,
}
const reviewStatusFilterValues = [
  'all',
  CustomerReviewStatus.Pending.Value,
  CustomerReviewStatus.Published.Value,
  CustomerReviewStatus.Hidden.Value,
  CustomerReviewStatus.Rejected.Value,
]
const reviewReadStateFilterValues = ['all', 'unread', 'read']
const reviewSortValues = [
  'received_desc',
  'received_asc',
  'rating_desc',
  'rating_asc',
  'reviewer_asc',
  'reviewer_desc',
  'status_asc',
  'status_desc',
]

const reviewStatusFilterOptions = [
  { value: 'all', label: 'All statuses', icon: SlidersHorizontal },
  { value: CustomerReviewStatus.Pending.Value, label: 'Pending', icon: MessageSquareText },
  { value: CustomerReviewStatus.Published.Value, label: 'Public', icon: BadgeCheck },
  { value: CustomerReviewStatus.Hidden.Value, label: 'Hidden', icon: EyeOff },
  { value: CustomerReviewStatus.Rejected.Value, label: 'Ignored', icon: XCircle },
]
const reviewReadStateFilterOptions = [
  { value: 'all', label: 'All read states', icon: SlidersHorizontal },
  { value: 'unread', label: 'Unread', icon: Mail },
  { value: 'read', label: 'Read', icon: CheckCircle2 },
]
const reviewSortOptions = [
  { value: 'received_desc', label: 'Received Newest', icon: CalendarClock },
  { value: 'received_asc', label: 'Received Oldest', icon: CalendarClock },
  { value: 'rating_desc', label: 'Rating High-Low', icon: Star },
  { value: 'rating_asc', label: 'Rating Low-High', icon: Star },
  { value: 'reviewer_asc', label: 'Reviewer A-Z', icon: ArrowDownAZ },
  { value: 'reviewer_desc', label: 'Reviewer Z-A', icon: ArrowDownAZ },
  { value: 'status_asc', label: 'Status Pending First', icon: BadgeCheck },
  { value: 'status_desc', label: 'Status Ignored First', icon: BadgeCheck },
]

const reviewIndexSortColumns = {
  reviewer: {
    asc: 'reviewer_asc',
    desc: 'reviewer_desc',
    defaultSort: 'reviewer_asc',
  },
  rating: {
    asc: 'rating_asc',
    desc: 'rating_desc',
    defaultSort: 'rating_desc',
  },
  status: {
    asc: 'status_asc',
    desc: 'status_desc',
    defaultSort: 'status_asc',
  },
  received: {
    asc: 'received_asc',
    desc: 'received_desc',
    defaultSort: 'received_desc',
  },
}

const reviewListUrlSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  status: z.enum(reviewStatusFilterValues).catch(defaultReviewListSearchParams.status),
  read_state: z.enum(reviewReadStateFilterValues).catch(defaultReviewListSearchParams.read_state),
  sort: z.enum(reviewSortValues).catch(defaultReviewListSearchParams.sort),
  page: z.coerce.number().int().positive().catch(defaultReviewListSearchParams.page),
  page_size: z.coerce
    .number()
    .int()
    .positive()
    .max(100)
    .catch(defaultReviewListSearchParams.page_size),
})

export const Route = createFileRoute('/_authenticated/$orgId/customer-reviews/')({
  component: RouteComponent,
  validateSearch: (search) => reviewListUrlSearchSchema.parse(search),
  search: {
    middlewares: [stripSearchParams(strippedReviewListSearchParams)],
  },
  loader: async ({ context, params }) => {
    if (!context.ability.can('list', 'customer-reviews')) throw notFound()
    void prefetchIndexTableRouteQuery(
      context.queryClient,
      customerReviewQueries.index({
        organizationId: params.orgId,
        filters: getReviewListFilters({}),
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      })
    )
  },
})

function getReviewsFromResponse(response) {
  return Array.isArray(response) ? response : (response?.data ?? [])
}

function getReviewListSearchParams(search = {}) {
  return {
    ...defaultReviewListSearchParams,
    ...search,
  }
}

function getReviewListFilters(search) {
  const parsedSearch = getReviewListSearchParams(search)
  const searchTerm = parsedSearch.search?.trim()

  return {
    ...(searchTerm ? { search: searchTerm } : {}),
    status: parsedSearch.status,
    read_state: parsedSearch.read_state,
    sort: parsedSearch.sort,
  }
}

function reviewCountText(total) {
  return `${total} review${total === 1 ? '' : 's'}`
}

function getReviewIndexSummary(response) {
  if (Array.isArray(response)) {
    return {
      average_rating: null,
      review_count: 0,
    }
  }

  return (
    response?.summary ?? {
      average_rating: null,
      review_count: 0,
    }
  )
}

function formatAverageRating(rating) {
  const normalizedRating = Number(rating)

  if (!Number.isFinite(normalizedRating)) {
    return null
  }

  return normalizedRating.toFixed(1)
}

function CustomerReviewIndexSummaryBadge({ summary }) {
  const reviewCount = Number(summary?.review_count ?? 0)
  const averageRating = formatAverageRating(summary?.average_rating)

  if (!reviewCount || averageRating === null) {
    return null
  }

  return (
    <div className="inline-flex max-w-full flex-wrap items-center gap-2 rounded-full bg-primary/10 px-2.5 py-1 text-interaction">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-accentSecondary text-interaction dark:bg-accentSecondary dark:text-interaction">
        <Star className="size-3.5 fill-current" />
      </span>
      <span className="font-semibold tabular-nums">{averageRating}</span>
      <span className="text-xs font-medium text-muted-foreground">average rating</span>
      <span className="text-xs text-muted-foreground">
        from {reviewCount} non-ignored {reviewCount === 1 ? 'review' : 'reviews'}
      </span>
    </div>
  )
}

function ReviewStatusSelect({ value, onValueChange }) {
  const isFiltered = value !== 'all'

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger aria-label="Filter review status" selected={isFiltered} className="w-fit">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {reviewStatusFilterOptions.map((option) => {
          const Icon = option.icon

          return (
            <SelectItem key={option.value} value={option.value}>
              <Icon className="size-4" />
              {option.label}
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}

function ReviewReadStateSelect({ value, onValueChange }) {
  const isFiltered = value !== 'all'

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger aria-label="Filter review read state" selected={isFiltered} className="w-fit">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {reviewReadStateFilterOptions.map((option) => {
          const Icon = option.icon

          return (
            <SelectItem key={option.value} value={option.value}>
              <Icon className="size-4" />
              {option.label}
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}

function ReviewSortSelect({ value, onValueChange }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger aria-label="Sort reviews" className="w-fit">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {reviewSortOptions.map((option) => {
          const Icon = option.icon

          return (
            <SelectItem key={option.value} value={option.value}>
              <Icon className="size-4" />
              {option.label}
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}

function getReviewIndexColumns() {
  return [
    {
      id: 'reviewer',
      header: 'Reviewer',
      cell: ({ row }) => {
        const review = row.original
        const isRead = Boolean(review.is_read)
        const primaryTextClass = isRead
          ? 'font-normal text-muted-foreground'
          : 'font-semibold text-foreground'

        return (
          <>
            <div className="flex min-w-0 items-center gap-2">
              {!isRead ? <span className="size-2 shrink-0 rounded-full bg-primary" /> : null}
              <span
                data-read-state={isRead ? 'read' : 'unread'}
                className={cn('block truncate', primaryTextClass)}
              >
                {review.reviewer_name}
              </span>
            </div>
            {review.reviewer_email ? (
              <Typography variant="caption" className="mt-1 block truncate">
                {review.reviewer_email}
              </Typography>
            ) : null}
          </>
        )
      },
      meta: {
        label: 'Reviewer',
        icon: UserRound,
        sortable: reviewIndexSortColumns.reviewer,
        width: '15rem',
      },
    },
    {
      id: 'review',
      header: 'Review',
      cell: ({ row }) => {
        const review = row.original
        const isRead = Boolean(review.is_read)
        const title = review.title || `${review.rating}-star review`

        return (
          <div className="min-w-0">
            <div
              className={cn(
                'truncate font-medium',
                isRead ? 'text-muted-foreground' : 'text-foreground'
              )}
            >
              {title}
            </div>
            {review.body ? (
              <Typography variant="caption" className="mt-1 block truncate">
                {review.body}
              </Typography>
            ) : null}
          </div>
        )
      },
      meta: {
        label: 'Review',
        icon: MessageSquareText,
        contentClassName: 'lg:w-full',
      },
    },
    {
      id: 'rating',
      header: 'Rating',
      cell: ({ row }) => <CustomerReviewStars rating={row.original.rating} />,
      meta: {
        label: 'Rating',
        icon: Star,
        sortable: reviewIndexSortColumns.rating,
        align: 'center',
        width: '7rem',
      },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const review = row.original

        return (
          <div className="flex flex-col items-start gap-1.5 lg:items-center">
            <CustomerReviewStatusBadge status={review.status} />
            {review.is_verified_purchase ? (
              <Badge variant="outline" className="whitespace-nowrap">
                Verified Purchaser
              </Badge>
            ) : null}
          </div>
        )
      },
      meta: {
        label: 'Status',
        icon: BadgeCheck,
        sortable: reviewIndexSortColumns.status,
        align: 'center',
        width: '8.5rem',
      },
    },
    {
      id: 'read',
      header: 'Read',
      cell: ({ row }) => {
        const isRead = Boolean(row.original.is_read)

        return <Badge variant={isRead ? 'outline' : 'default'}>{isRead ? 'Read' : 'Unread'}</Badge>
      },
      meta: {
        label: 'Read',
        icon: Mail,
        align: 'center',
        width: '7rem',
      },
    },
    {
      id: 'received',
      header: 'Received',
      cell: ({ row }) => {
        const review = row.original
        const isRead = Boolean(review.is_read)
        const primaryTextClass = isRead
          ? 'font-normal text-muted-foreground'
          : 'font-semibold text-foreground'

        return (
          <span
            data-read-state={isRead ? 'read' : 'unread'}
            className={cn('whitespace-nowrap tabular-nums', primaryTextClass)}
          >
            {dayjs(review.created_at).format('MM/DD/YYYY')}
          </span>
        )
      },
      meta: {
        label: 'Received',
        icon: CalendarClock,
        sortable: reviewIndexSortColumns.received,
        align: 'center',
        width: '8.5rem',
      },
    },
  ]
}

function getReviewIndexRowPresentation(review) {
  return { attention: review.is_read ? undefined : 'unread' }
}

export function RouteComponent() {
  const { orgId } = Route.useParams()
  const path = usePathHelper()
  const navigate = useNavigate()
  const urlSearch = getReviewListSearchParams(Route.useSearch())
  const urlSearchTerm = urlSearch.search ?? ''
  const { updatePage, updatePageSize, updateSearchTerm, updateSort, updateTableParams } =
    useIndexTableSearchParams({
      navigate,
      getSearchParams: getReviewListSearchParams,
      searchTerm: urlSearchTerm,
    })
  const [showGlobalAlert, setShowGlobalAlert] = useState(true)
  const reviewFilters = getReviewListFilters(urlSearch)
  const hasActiveSearch = urlSearchTerm.trim().length > 0
  const hasClearableFilters =
    hasActiveSearch ||
    urlSearch.status !== defaultReviewListSearchParams.status ||
    urlSearch.read_state !== defaultReviewListSearchParams.read_state
  const {
    data: reviewsResponse,
    isFetching,
    isPending,
  } = useCustomerReviews({
    organizationId: orgId,
    filters: reviewFilters,
    page: urlSearch.page,
    pageSize: urlSearch.page_size,
  })
  const reviews = getReviewsFromResponse(reviewsResponse)
  const reviewSummary = getReviewIndexSummary(reviewsResponse)
  const paginationMeta = getIndexPaginationMeta(
    reviewsResponse,
    urlSearch.page,
    urlSearch.page_size
  )
  const columns = useMemo(() => getReviewIndexColumns(), [])

  const handleStatusChange = (value) => updateTableParams({ status: value })

  const handleReadStateChange = (value) => updateTableParams({ read_state: value })

  const clearFilters = () =>
    updateTableParams({
      search: undefined,
      status: defaultReviewListSearchParams.status,
      read_state: defaultReviewListSearchParams.read_state,
    })

  const handlePageIntent = (page) => {
    void queryClient.prefetchQuery(
      customerReviewQueries.index({
        organizationId: orgId,
        filters: reviewFilters,
        page,
        pageSize: urlSearch.page_size,
      })
    )
  }

  return (
    <div className="space-y-4">
      <IfGlobalOrg>
        {showGlobalAlert ? (
          <OrganizationContextNotice className="mb-4" onDismiss={() => setShowGlobalAlert(false)}>
            Reviews are florist-specific.
          </OrganizationContextNotice>
        ) : null}
      </IfGlobalOrg>

      <AdminPageHeader
        icon={Star}
        title="Customer Reviews"
        description="Review customer feedback before it appears on the storefront."
        metrics={<CustomerReviewIndexSummaryBadge summary={reviewSummary} />}
      />

      <div className="space-y-4">
        <IndexDataTable
          data={reviews}
          columns={columns}
          sort={urlSearch.sort}
          onSortChange={updateSort}
          rowLink={(review) => path.customerReviews.show(review.id).link}
          getRowPresentation={getReviewIndexRowPresentation}
          getRowId={(review) => String(review.id)}
          isLoading={isPending}
          toolbar={
            <IndexTableToolbar
              primary={
                <IndexTableSearchBox
                  value={urlSearchTerm}
                  onCommit={updateSearchTerm}
                  countText={reviewCountText(paginationMeta.total)}
                  isFetching={isFetching}
                  placeholder="Search reviews..."
                />
              }
              secondary={
                <>
                  {hasClearableFilters ? (
                    <IndexTableClearFiltersButton onClick={clearFilters} />
                  ) : null}
                  <ReviewStatusSelect value={urlSearch.status} onValueChange={handleStatusChange} />
                  <ReviewReadStateSelect
                    value={urlSearch.read_state}
                    onValueChange={handleReadStateChange}
                  />
                  <div className="lg:hidden">
                    <ReviewSortSelect value={urlSearch.sort} onValueChange={updateSort} />
                  </div>
                </>
              }
            />
          }
          emptyState={<Typography variant="supporting">No reviews found.</Typography>}
          tableClassName="text-sm lg:min-w-[62rem] lg:table-fixed"
        />

        <IndexTablePagination
          meta={paginationMeta}
          pageSize={urlSearch.page_size}
          onPageChange={updatePage}
          onPageIntent={handlePageIntent}
          onPageSizeChange={updatePageSize}
        />
      </div>
    </div>
  )
}
